import React, { useState, useEffect } from 'react';
import { Shield, Github, Search, Loader, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import { useScan } from '../contexts/ScanContext';
import securityApi from '../api/securityApi';
import RemoveInstallationModal from './RemoveInstallationModal';
import ConnectGitHubButton from './ConnectGitHubButton';

const RepositorySelector = () => {
  const { startScan } = useScan();
  // Repository state
  const [oauthRepositories, setOauthRepositories] = useState([]);
  const [appRepositories, setAppRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [isGithubAuthenticated, setIsGithubAuthenticated] = useState(false);
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const reposPerPage = 5;
  // GitHub App state
  const [githubAppInfo, setGithubAppInfo] = useState(null);
  const [githubAppInstallations, setGithubAppInstallations] = useState([]);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [useGithubApp, setUseGithubApp] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState(null);


  useEffect(() => {
    // Load GitHub App info and check GitHub auth
    // loadGithubAppInfo();
    checkGithubAuth();
  }, []);

  useEffect(() => {
    // Load GitHub App info if user is authenticated
    if (isGithubAuthenticated) {
      loadGithubAppInfo();
    }
  }, [isGithubAuthenticated]);

  useEffect(() => {
    // Check for GitHub App installation callback
    const urlParams = new URLSearchParams(window.location.search);
    const githubAppInstalled = urlParams.get('github_app_installed');
    const githubAppUpdated = urlParams.get('github_app_updated');
    const installationError = urlParams.get('error');

    if (githubAppInstalled === 'true' || githubAppUpdated === 'true') {
      // Installation was successful, refresh the installations list
      if (isGithubAuthenticated) {
        loadGithubAppInfo();
        // Switch to GitHub App mode
        setUseGithubApp(true);
        // Clean up URL parameters only after processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (installationError) {
      // Handle installation error
      setError(`GitHub App installation error: ${installationError}`);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isGithubAuthenticated]);

  const loadGithubAppInfo = async () => {
    try {
      // Get GitHub App info
      const appInfo = await securityApi.getGithubAppInfo();
      setGithubAppInfo(appInfo);

      // If app is configured, try to get installations
      if (appInfo && appInfo.app_id) {
        try {
          const installations = await securityApi.getGithubAppInstallations();
          console.log("Installations :", installations)
          setGithubAppInstallations(installations.installations || []);
          // No need to set installationConfigUrl here, will be derived from selectedInstallation
        } catch (err) {
          console.error('Error loading GitHub App installations:', err);
          // Non-critical error, don't show to user
        }
      }
    } catch (err) {
      console.error('Error loading GitHub App info:', err);
      // Non-critical error, don't show to user
    }
  };

  const handleRemoveInstallation = async () => {
    if (!selectedInstallation) return;
    setRemoveLoading(true);
    setRemoveError(null);
    try {
      await securityApi.deleteGithubAppInstallation(selectedInstallation);
      setSelectedInstallation(null);
      setAppRepositories([]);
      setShowRemoveModal(false);
      await loadGithubAppInfo();
    } catch (err) {
      setRemoveError(err.response?.data?.detail || err.message || 'Failed to remove installation');
    } finally {
      setRemoveLoading(false);
    }
  };

  const checkGithubAuth = async () => {
    try {
      setLoading(true);
      // First check if the user is authenticated with GitHub
      const authStatus = await securityApi.checkGithubAuth();
      setIsGithubAuthenticated(authStatus.authenticated);

      // If authenticated, load repositories
      if (authStatus.authenticated) {
        await loadRepositories();
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking GitHub auth:', err);
      setError('Failed to check GitHub authentication status');
      setLoading(false);
    }
  };

  // Load repositories based on current mode
  const loadRepositories = async () => {
    try {
      if (useGithubApp) {
        if (selectedInstallation) {
          // Load repositories from GitHub App installation
          await loadAppRepositories(selectedInstallation);
        } else if (appRepositories.length === 0) {
          // If no installation selected and no app repos loaded, show empty state
          setLoading(false);
        }
      } else {
        // Load repositories from OAuth
        await loadOAuthRepositories();
      }
    } catch (err) {
      if (err.message === 'GitHub authentication required') {
        setIsGithubAuthenticated(false);
        setError('GitHub authentication required to view repositories');
      } else if (err.response?.status === 401 && err.response?.data?.detail === "GitHub token is invalid or expired") {
        // Handle case where token exists but doesn't have sufficient permissions
        setIsGithubAuthenticated(true);
        setError('Your GitHub token lacks necessary permissions.');
      } else {
        setError(err.message);
      }
      setLoading(false);
    }
  };

  // Load repositories from OAuth
  const loadOAuthRepositories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityApi.getGithubRepositories();
      setOauthRepositories(response.repositories || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading OAuth repositories:', err);
      setError('Failed to load repositories from GitHub');
      setLoading(false);
      throw err;
    }
  };

  // Load repositories from GitHub App installation
  const loadAppRepositories = async (installationId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityApi.getGithubAppRepositories(installationId);
      setAppRepositories(response.repositories || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading repositories from installation:', err);
      setError('Failed to load repositories from GitHub App installation');
      setLoading(false);
      throw err;
    }
  };

  // Handle installation selection
  const handleInstallationSelect = (installationId) => {
    setSelectedInstallation(installationId);
    if (installationId) {
      loadAppRepositories(installationId);
      // Find the selected installation object to get the account URL
      const selectedInst = githubAppInstallations.find(inst => inst.id === Number(installationId));
      // No need to set installationConfigUrl here, will be derived from selectedInstallation
    } else {
      loadAppRepositories(null); // Clear repositories if no installation selected
    }
  };

  // Toggle between OAuth and GitHub App
  const handleToggleRepositorySource = () => {
    const newUseGithubApp = !useGithubApp;
    setUseGithubApp(newUseGithubApp);
    setSelectedRepo(null);
    setError(null);
    setCurrentPage(1);

    if (newUseGithubApp) {
      // Switching to GitHub App
      if (githubAppInstallations.length === 0) {
        setError('No GitHub App installations found. Please install the app first.');
      } else if (selectedInstallation && appRepositories.length === 0) {
        // If installation selected but no repos loaded yet
        loadAppRepositories(selectedInstallation);
      }
    } else {
      // Switching to OAuth
      if (oauthRepositories.length === 0) {
        loadOAuthRepositories();
      }
    }
  };

  const handleRepoSelect = (repo) => {
    // If this is a GitHub App repository, make sure it has the necessary properties for scanning
    if (useGithubApp && repo) {
      // Ensure the repository has html_url property which is needed for scanning
      if (!repo.html_url && repo.url) {
        // If html_url is missing but we have url, use that instead
        repo = { ...repo, html_url: repo.url };
      }
    }

    // Toggle selection (select if different repo, deselect if same repo)
    setSelectedRepo(selectedRepo?.id === repo.id ? null : repo);
  };

  const handleStartScan = async () => {
    if (!selectedRepo) return;
    try {
      // Determine the repository URL to use for scanning
      let repoUrl = selectedRepo.html_url;

      // If html_url is not available, try to use other URL properties
      if (!repoUrl) {
        if (selectedRepo.url) {
          repoUrl = selectedRepo.url;
        } else if (selectedRepo.clone_url) {
          repoUrl = selectedRepo.clone_url;
        } else if (selectedRepo.git_url) {
          repoUrl = selectedRepo.git_url;
        } else if (selectedRepo.ssh_url) {
          repoUrl = selectedRepo.ssh_url;
        } else {
          // Construct URL from full_name if available
          if (selectedRepo.full_name) {
            repoUrl = `https://github.com/${selectedRepo.full_name}`;
          }
        }
      }

      if (!repoUrl) {
        throw new Error("Could not determine repository URL for scanning");
      }

      console.log('Starting scan for:', repoUrl); // Debugging line
      
      // Pass the installation ID if using GitHub App
      const installationId = useGithubApp ? selectedInstallation : null;
      await startScan(repoUrl, selectedRepo.private, installationId);
    } catch (err) {
      setError(`Failed to start scan: ${err.message}`);
    }
  };

  // Get the active repository list based on current mode
  const activeRepositories = useGithubApp ? appRepositories : oauthRepositories;

  // Calculate pagination
  const filteredRepos = activeRepositories.filter(repo =>
    repo.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastRepo = currentPage * reposPerPage;
  const indexOfFirstRepo = indexOfLastRepo - reposPerPage;
  const currentRepos = filteredRepos.slice(indexOfFirstRepo, indexOfLastRepo);
  const totalPages = Math.ceil(filteredRepos.length / reposPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <Loader className="w-6 h-6 text-teal-600 animate-spin" />
          <span className="text-gray-700">Loading repositories...</span>
        </div>
      </div>
    );
  }

  // If not authenticated with GitHub, show login prompt
  if (!isGithubAuthenticated) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">GitHub Authentication Required</h2>
        <p className="text-gray-600 mb-6">
          To select a repository from your GitHub account, you need to authenticate with GitHub first.
        </p>
        <div className="flex justify-center">
          {/* forceLogin preserves this gate's pre-existing behaviour: we get
              here only when checkGithubAuth() said the session is not
              authenticated, so re-prompting is the intent. */}
          <ConnectGitHubButton
            variant="oauth"
            label="Connect to GitHub"
            forceLogin
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-gray-600 mb-6 text-center">
        Select the repository you want Fenny to analyze
      </p>

      {/* GitHub App toggle */}
      {githubAppInfo && githubAppInfo.app_id && (
        <div className="mb-6">
          <div className="flex justify-center mb-4 w-full">
            <div className="bg-gray-100 p-1 rounded-lg flex flex-col w-full sm:flex-row sm:w-auto">
              <button
                onClick={() => handleToggleRepositorySource()}
                className={`w-full sm:w-auto justify-center px-4 py-2 min-h-[44px] rounded-lg flex items-center ${!useGithubApp
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Github className="w-4 h-4 mr-2" />
                OAuth Repositories
              </button>
              <button
                onClick={() => handleToggleRepositorySource()}
                className={`w-full sm:w-auto justify-center px-4 py-2 min-h-[44px] rounded-lg flex items-center ${useGithubApp
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Shield className="w-4 h-4 mr-2" />
                GitHub App
              </button>
            </div>
          </div>

          {useGithubApp && (
            <div className="mb-4">
              {githubAppInstallations.length > 0 ? (
                <div>
                  <label className="block text-gray-700 mb-2">Select Installation:</label>
                  <select
                    value={selectedInstallation || ''}
                    onChange={(e) => handleInstallationSelect(e.target.value ? Number(e.target.value) : null)}
                    className="w-full bg-gray-50 text-gray-900 border border-gray-300 rounded-lg p-3 focus:outline-none focus:border-teal-500"
                  >
                    <option value="">Select an installation</option>
                    {githubAppInstallations.map(installation => (
                      <option key={installation.id} value={installation.id}>
                        {installation.account.login} ({installation.account.type})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="text-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-600 mb-3">No GitHub App installations found.</p>
                </div>
              )}
              {/* One install CTA, whose wording depends on whether anything is
                  already installed. Previously two of these rendered at once
                  when there were no installations. */}
              <div className="mt-4 flex justify-center">
                <ConnectGitHubButton
                  variant="app"
                  installationUrl={githubAppInfo?.installation_url}
                  label={
                    githubAppInstallations.length > 0
                      ? 'Install on another account'
                      : 'Install GitHub App'
                  }
                  fullWidth={false}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search repositories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-50 text-gray-900 border border-gray-300 rounded-lg p-3 pl-10 focus:outline-none focus:border-teal-500"
        />
      </div>

      {/* Repository list */}
      <div className="space-y-3 mb-6">
        {error ? (
          <div className="text-center text-red-600 mb-4">
            {error}
            {error.includes('No GitHub App installations found') && githubAppInfo && (
              <div className="mt-3 flex justify-center">
                <ConnectGitHubButton
                  variant="app"
                  installationUrl={githubAppInfo.installation_url}
                  fullWidth={false}
                />
              </div>
            )}
          </div>
        ) : activeRepositories.length === 0 ? (
          <div className="text-center text-gray-600 p-6 bg-gray-50 border border-gray-200 rounded-lg">
            {useGithubApp ?
              selectedInstallation ? (
                <>
                  <p className="mb-4">No repositories found for this installation. Make sure you've granted access to the repositories you want to scan.</p>
                  {/* Link to manage repository access on GitHub for the selected installation */}
                  {selectedInstallation && githubAppInfo && githubAppInstallations.length > 0 &&
                    (() => {
                      const currentInstallation = githubAppInstallations.find(inst => inst.id === Number(selectedInstallation));
                      if (currentInstallation && currentInstallation.account && githubAppInfo.app_name) {
                        const settingsUrl = `https://github.com/settings/installations/${selectedInstallation}`;
                        return (
                          <a
                            href={settingsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white mx-auto inline-flex items-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Manage Repository Access
                          </a>
                        );
                      }
                      return null;
                    })()
                  }
                </>
              ) :
                "Please select a GitHub App installation above."
              :
              "No repositories found. Try adjusting your search or check your GitHub permissions."
            }
          </div>
        ) : (
          currentRepos.map(repo => (
            <div
              key={repo.id}
              onClick={() => handleRepoSelect(repo)}
              className={`p-4 rounded-lg transition-all cursor-pointer ${selectedRepo?.id === repo.id
                  ? 'bg-teal-50 border-teal-400'
                  : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                } border`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3 min-h-[48px] min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
                    <Github className="w-6 h-5 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-gray-900 font-medium break-words">{repo.full_name}</h3>
                    {repo.description && (
                      <p className="text-gray-500 text-sm">{repo.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {repo.private && (
                    <span className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-1 rounded">
                      Private
                    </span>
                  )}
                  {repo.installation_id && (
                    <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded">
                      App
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Simplified Pagination */}
      {!error && filteredRepos.length > reposPerPage && (
        <div className="flex justify-between gap-3 mb-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Refresh and GitHub permission buttons */}
      <div className="flex flex-wrap justify-center mb-6 gap-4">
        <button
          onClick={() => {
            setError(null);
            if (useGithubApp) {
              if (selectedInstallation) {
                loadAppRepositories(selectedInstallation);
              } else {
                loadGithubAppInfo();
              }
            } else {
              loadOAuthRepositories();
            }
          }}
          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          <span>Refresh Repositories</span>
        </button>

        {/* Button to update GitHub permissions */}
        {/* <button
          onClick={() => {
            // Force GitHub re-authorization with updated permissions
            securityApi.initiateGithubAuth(true);
          }}
          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center"
        >
          <LogIn className="h-4 w-4 mr-2" />
          <span>Update GitHub Permissions</span>
        </button> */}
      </div>

      {/* Repository management links (only shown when using GitHub App) */}
      {useGithubApp && selectedInstallation && githubAppInfo && githubAppInstallations.length > 0 &&
        (() => {
          const currentInstallation = githubAppInstallations.find(inst => inst.id === Number(selectedInstallation));
          if (currentInstallation && currentInstallation.account && githubAppInfo.app_name) {
            const settingsUrl = `https://github.com/settings/installations/${currentInstallation.id}`;
            return (
              <div className="flex flex-col sm:flex-row justify-center gap-3 mb-6">
                <a
                  href={settingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 min-h-[44px] rounded-lg flex items-center justify-center space-x-2 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <ExternalLink className="h-5 w-5 mr-2" />
                  <span>Manage Repository Access on GitHub</span>
                </a>
                <button
                  onClick={() => setShowRemoveModal(true)}
                  className="px-6 py-3 min-h-[44px] rounded-lg flex items-center justify-center space-x-2 text-red-600 border border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-5 w-5 mr-2" />
                  <span>Remove Installation</span>
                </button>
              </div>
            );
          }
          return null;
        })()
      }

      {/* Action button */}
      <div className="flex justify-end">
        <button
          onClick={handleStartScan}
          disabled={!selectedRepo}
          className={`w-full sm:w-auto justify-center px-6 py-3 min-h-[44px] rounded-lg flex items-center space-x-2 text-white ${!selectedRepo
              ? 'bg-teal-600/50 cursor-not-allowed'
              : 'bg-teal-600 hover:bg-teal-700'
            }`}
        >
          <Shield className="h-5 w-5 mr-2" />
          <span>Start Scan</span>
        </button>
      </div>

      <RemoveInstallationModal
        isOpen={showRemoveModal}
        accountLogin={
          githubAppInstallations.find(inst => inst.id === Number(selectedInstallation))?.account?.login || ''
        }
        onConfirm={handleRemoveInstallation}
        onClose={() => { setShowRemoveModal(false); setRemoveError(null); }}
        isLoading={removeLoading}
        error={removeError}
      />
    </div>
  );
};

export default RepositorySelector;